import { OrdersSliceState } from '../../../slices/orders-slice';
import { XmlStructureOrderInfoData } from '../../../xml/model/xml-structure-model';
import { VisibleConditionValueGetter } from '../../../manager/visible-condition-value-getter';

/**
 * 保存済み XML 構造（注文 1 件 + その配下の全 order-info-data）を
 * 注文作成画面の state 形（OrdersSliceState）へ復元する。
 *
 * 「注文編集」ボタン・お気に入り商品保存・注文確認ダイアログ等、
 * XML から作成時の選択値を復元したい箇所で共通利用する純粋関数。
 * 副作用・API 呼び出しは無い。
 *
 * （旧 image-upload.tsx から切り出し。image-upload.tsx は後方互換のため再 export している）
 */
export const convertResponseToOrderData = (
  _order: XmlStructureOrderInfoData,
  _options: XmlStructureOrderInfoData[],
  visibleCondition: VisibleConditionValueGetter,
  option?: {
    // 1: ワンストップ 2: Sift 3: OLC
    kind?: 1 | 2 | 3,
  }
): Partial<OrdersSliceState> => {
  const order = _order.xml;
  const cover = _order.parts?.partsData?.find((v) => v.xml.viewModel && !v.xml.viewModel.type)?.xml;
  const genuine = _order.parts?.partsData?.find((v) => v.xml.viewModel && v.xml.viewModel.type === 'main')?.xml;
  const options = _options.filter((v) => v.xml.metaModel.parentId === order.metaModel.id);
  const descriptionInfo = order.viewModel.descriptionInfo;
  const descriptionOpInfo = order.optionOrderInfoDataXml.find(v => v.viewModel.descriptionInfo?.pageType);
  const isEmpty = !Object.keys(descriptionInfo?.pageType ?? {}).length;
  const targetDescriptionInfo = !isEmpty ? descriptionInfo : descriptionOpInfo?.viewModel.descriptionInfo;
  return ({
    itemInfo: order.viewModel.category && {
      category: { value: order.viewModel.category?.id || '', label: order.viewModel.category?.name || '' },
      tag: { value: order.viewModel.productTagId || '', label: order.viewModel.productTagId || '' },
    },
    itemDetail: {
      coverSelect: { value: order.viewModel.coverId || '', label: '' },
      coverColorSelect: {
        value: order.viewModel.coverColorId || cover?.viewModel.cover?.color || '',
        label: cover?.viewModel.cover?.colorName || '',
      },
      itemSelect: { value: order.viewModel.item?.id || '', label: order.viewModel.item?.name || '' },
      boardSelect: { value: order.viewModel.boardInfo || '', label: '' },
      orderMethod: { value: order.viewModel.orderMethod?.id || '', label: order.viewModel.orderMethod?.name || '' },
      designType1: { value: order.viewModel.designType?.templateGroupId || '', label: '' },
      designType2: {
        value: order.viewModel.designType?.templateId || '',
        label: order.viewModel.designType?.name || '',
      },
      // quantity: Number(res.infoOutputCount) || 0,
      quantity: Number(order.viewModel.infoOutputCount) || 0,
      ...option?.kind === 3 ? {
        purpose: {
          label: order.viewModel.purpose?.name ?? '',
          value: order.viewModel.purpose?.id ?? '',
        }
      } : {}
    },
    descriptionInfo: targetDescriptionInfo && {
      name: targetDescriptionInfo.name,
      date: { value: targetDescriptionInfo.date, alert: false },
      formatType: '',
      formatCase: '',
      photographLocation: targetDescriptionInfo.location,
      descriptionLocations: targetDescriptionInfo.pageType && Object.keys(targetDescriptionInfo.pageType || {}).map((v) => v),
    },
    cover: cover && {
      laminate: {
        value: cover.viewModel.infoSurface?.laminateID || '',
        label: cover.viewModel.infoSurface?.name || '',
      },
      color: { value: cover.viewModel.cover?.color || '', label: cover.viewModel.cover?.colorName || '' },
      coverProcessing: { value: cover.viewModel.leaf?.color || '', label: cover.viewModel.leaf?.colorName || '' },
      leafText1: cover.viewModel.leaf?.text1 || '',
      leafText2: cover.viewModel.leaf?.text2 || '',
    },
    genuine: genuine && {
      laminate: {
        value: genuine.viewModel.infoSurface?.laminateID || '',
        label: genuine.viewModel.infoSurface?.name || '',
      },
      genuineProcessing: { value: genuine.viewModel.leaf?.color || '', label: genuine.viewModel.leaf?.colorName || '' },
      leafText1: genuine.viewModel.leaf?.text1 || '',
      leafText2: genuine.viewModel.leaf?.text2 || '',
      additional: { value: genuine.viewModel.additionalInfo?.[0] || '', label: '' },
      additionalMulti: genuine.viewModel.additionalInfo?.[0] || '', // TODO
      chronologies: {
        value: genuine.viewModel.chronology?.chronologyID || '',
        label: genuine.viewModel.chronology?.chronologyName || '',
      },
      page: Number(genuine.viewModel.pageCount) || 1,
    },
    useImage: {
      image: [],
    },
    option: options.length ? [...options].map((v, i) => ({
      tag: { value: v.xml.viewModel.productTagId || '', label: v.xml.viewModel.productTagId || '' },
      item: {
        value: v.xml.viewModel.item?.id || '',
        label: v.xml.viewModel.item?.name || '',
        serviceId: v.xml.viewModel.category?.id || '',
        serviceName: v.xml.viewModel.category?.name || '',
      },
      board: { value: v.xml.viewModel.boardInfo || '', label: '' }, // TODO
      quantity: Number(v.xml.viewModel.infoOutputCount) || 1,
      cover: v.parts?.partsData?.[0]?.xml.viewModel.infoSurface?.laminateID ? { // TODO
        laminate: { value: v.parts.partsData[0].xml.viewModel.infoSurface.laminateID, label: '' },
      } : { laminate: { value: '', label: '' } },
      genuine: v.parts?.partsData?.[1]?.xml.viewModel.infoSurface?.laminateID ? { // TODO
        laminate: { value: v.parts.partsData[1].xml.viewModel.infoSurface.laminateID, label: '' },
      } : { laminate: { value: '', label: '' } },
      descriptionLocations: Object.keys(v.xml.viewModel.descriptionInfo?.pageType || {}).map((key) => key),
      layoutFinish: v.xml.metaModel.layoutFinish || '',
      partsCount: '',
      edit: true,
      foilStamping: { value: v.parts?.partsData?.[0]?.xml.viewModel.leaf?.color ?? '', label: v.parts?.partsData?.[0]?.xml.viewModel.leaf?.colorName ?? '' },
      leafText1: v.parts?.partsData?.[0]?.xml.viewModel.leaf?.text1 ?? '',
      leafText2: v.parts?.partsData?.[0]?.xml.viewModel.leaf?.text2 ?? '',
      pageTypes: [],
    })) : [],
    remarks: {
      remarks: order.viewModel.remarks || '',
    },
    mailRemarks: {
      mailRemarks: order.viewModel.mailRemarks || '',
    },
    orderInfo: {
      proFlg: (() => {
        return Boolean(visibleCondition?.visibleData.orderInfo.o_isCategorySap?.visibleCondition?.find((v) => v.value === order.viewModel.category?.id));
      })(),
      imageNum: {
        min: 0,
        max: 0,
      },
    },
  });
};
